
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { Navigate, Link } from "react-router-dom";
import Header from '@/components/header/ModernHeader';
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useWishlist } from "@/hooks/useWishlist";
import { useEnhancedCart } from "@/hooks/useEnhancedCart";
import { Heart, ShoppingCart, Eye, Trash2, Search, SortAsc, Filter, Grid, List, ShoppingBag, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { generateProductUrl } from "@/utils/productUrlUtils";
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PremiumProductBadges from "@/components/Products/PremiumProductBadges";
import WishlistButton from "@/components/WishlistButton";
import BackButton from "@/components/navigation/BackButton";

const WishlistPage = () => {
  const { user, loading } = useOptimizedAuth();
  const { wishlist, isLoading: wishlistLoading, removeFromWishlist } = useWishlist();
  const { addToCartWithNotification } = useEnhancedCart();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const handleAddToCart = async (product: any) => {
    await addToCartWithNotification(product, 1);
  };

  const handleMoveAllToCart = async () => {
    for (const item of wishlist) {
      await addToCartWithNotification(item.products, 1);
    }
  };

  const handleClearWishlist = async () => {
    for (const item of wishlist) {
      removeFromWishlist(item.product_id);
    }
  };

  const filteredAndSortedWishlist = useMemo(() => {
    let filtered = wishlist.filter(item =>
      item.products.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (sortBy) {
      case "name":
        filtered.sort((a, b) => a.products.name.localeCompare(b.products.name));
        break;
      case "price-low":
        filtered.sort((a, b) => a.products.price - b.products.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.products.price - a.products.price);
        break;
      case "recent":
      default:
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }

    return filtered;
  }, [wishlist, searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full mx-auto"
          />
          <p className="mt-4 text-muted-foreground font-medium">Loading your wishlist...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
        <Header />
        <div className="container mx-auto px-4 pt-24 pb-8">
          <div className="max-w-7xl mx-auto">
            <BackButton to="/products" label="Back to Products" useSmartNavigation />
            {/* Header Section */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-primary to-primary-glow bg-clip-text text-transparent mb-4">
                  My Wishlist
                </h1>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Keep track of items you love and never miss out on your favorites
                </p>
              </motion.div>
            </div>

            {wishlistLoading ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <Skeleton className="h-12 w-full sm:w-80" />
                  <div className="flex gap-2">
                    <Skeleton className="h-10 w-32" />
                    <Skeleton className="h-10 w-24" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Card key={i} className="overflow-hidden">
                      <Skeleton className="h-64 w-full" />
                      <CardContent className="p-4 space-y-3">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-6 w-1/2" />
                        <div className="flex gap-2">
                          <Skeleton className="h-9 flex-1" />
                          <Skeleton className="h-9 flex-1" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : wishlist.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center py-16"
              >
                <div className="max-w-md mx-auto">
                  <div className="w-32 h-32 bg-gradient-to-br from-rose-100 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-16 h-16 text-rose-500" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Your wishlist is empty</h3>
                  <p className="text-muted-foreground mb-8 leading-relaxed">
                    Discover amazing products and save your favorites for later. Start building your dream collection today!
                  </p>
                  <Button asChild size="lg" className="bg-gradient-to-r from-primary to-primary-glow">
                    <Link to="/products">
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Explore Products
                    </Link>
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                {/* Controls Bar */}
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-1">
                        <div className="relative flex-1 max-w-md">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Search your wishlist..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 bg-background/50"
                          />
                          {searchTerm && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSearchTerm("")}
                              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="w-40 bg-background/50">
                            <SortAsc className="w-4 h-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recent">Recently Added</SelectItem>
                            <SelectItem value="name">Name A-Z</SelectItem>
                            <SelectItem value="price-low">Price Low-High</SelectItem>
                            <SelectItem value="price-high">Price High-Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 border rounded-lg p-1 bg-background/50">
                          <Button
                            variant={viewMode === "grid" ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setViewMode("grid")}
                            className="h-7 w-7 p-0"
                          >
                            <Grid className="w-3 h-3" />
                          </Button>
                          <Button
                            variant={viewMode === "list" ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setViewMode("list")}
                            className="h-7 w-7 p-0"
                          >
                            <List className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {wishlist.length > 0 && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleMoveAllToCart}
                              className="bg-background/50"
                            >
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Add All to Cart
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleClearWishlist}
                              className="bg-background/50 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Clear All
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/60">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-primary/10 text-primary">
                          {filteredAndSortedWishlist.length} {filteredAndSortedWishlist.length === 1 ? 'item' : 'items'}
                        </Badge>
                        {searchTerm && (
                          <span className="text-sm text-muted-foreground">
                            matching "{searchTerm}"
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Wishlist Items */}
                <AnimatePresence mode="wait">
                  {filteredAndSortedWishlist.length === 0 ? (
                    <motion.div
                      key="no-results"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-12"
                    >
                      <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-foreground mb-2">No items found</h3>
                      <p className="text-muted-foreground">Try adjusting your search or filters</p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="results"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className={viewMode === "grid" 
                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
                        : "space-y-4"
                      }
                    >
                      {filteredAndSortedWishlist.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Card className="group overflow-hidden border-border/60 hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10">
                            <div className="relative">
                              {/* <PremiumProductBadges product={item.products} /> */}
                              <img
                                src={item.products.image_url || '/placeholder.svg'}
                                alt={item.products.name}
                                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              
                              {/* Overlay Actions */}
                              <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                                <WishlistButton 
                                  productId={item.product_id} 
                                  variant="outline" 
                                  size="icon"
                                  className="bg-background/90 backdrop-blur-sm border-border/60"
                                />
                              </div>
                              
                              {/* Status Badge */}
                              <div className="absolute bottom-3 left-3">
                                <Badge 
                                  variant={item.products.status === 'active' ? 'default' : 'secondary'}
                                  className="bg-background/90 backdrop-blur-sm"
                                >
                                  {item.products.status}
                                </Badge>
                              </div>
                            </div>
                            
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div>
                                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                                    {item.products.name}
                                  </h3>
                                  <p className="text-2xl font-bold text-foreground mt-1">
                                    ${item.products.price.toFixed(2)}
                                  </p>
                                </div>
                                
                                <div className="flex gap-2">
                                  <Button asChild variant="outline" size="sm" className="flex-1">
                                    <Link to={`/product/${item.products.id}`}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      View Details
                                    </Link>
                                  </Button>
                                  <Button 
                                    variant="default" 
                                    size="sm" 
                                    className="flex-1 bg-gradient-to-r from-primary to-primary-glow"
                                    onClick={() => handleAddToCart(item.products)}
                                  >
                                    <ShoppingCart className="h-4 w-4 mr-2" />
                                    Add to Cart
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default WishlistPage;
